import type { ApolloCache } from '@apollo/client';
import { state } from '@frameio/graphql-client';
import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import type { FolderPermissionsFragment } from '@frameio/permissions/gql/generated/FolderPermissions';
import { FolderPermissions } from '@frameio/permissions/gql/generated/FolderPermissions';
import { isInteractiveLiveAssetType } from '@frameio/shared/media-viewer/hooks/useLiveUrl';
import type { AssetFiletypeFragment } from '../../../shared/gql/generated/AssetFiletype';
import { AssetFiletype } from '../../../shared/gql/generated/AssetFiletype';
import {
  AssetParent,
  type AssetParentFragment,
} from '../../../shared/gql/generated/AssetParent';
import type { DndOperation } from './types';

type VersionStackCandidate = {
  source: AssetParentFragment | null | undefined;
  target: AssetParentFragment | null | undefined;
  isCSCProject: boolean;
  /** source/target filetypes, only consulted for CSC projects */
  sourceFiletype?: string | null;
  targetFiletype?: string | null;
  isSourceUploading: boolean;
  isTargetUploading: boolean;
  canVersionChildren: boolean;
};

/**
 * Pure version-stack rules over already-resolved asset data. The Apollo cache
 * lookups that produce these inputs live in isValidVersionStackOp.
 */
export const canStackAssetsAsVersions = ({
  source,
  target,
  isCSCProject,
  sourceFiletype,
  targetFiletype,
  isSourceUploading,
  isTargetUploading,
  canVersionChildren,
}: VersionStackCandidate): boolean => {
  if (!source || !target) return false;

  // CSC projects can only version assets that share a filetype
  if (isCSCProject) {
    if (sourceFiletype == null || targetFiletype == null) return false;
    if (sourceFiletype !== targetFiletype) return false;
  }

  if (
    source.__typename === 'FolderAsset' ||
    target.__typename === 'FolderAsset' ||
    isInteractiveLiveAssetType(source.__typename) ||
    isInteractiveLiveAssetType(target.__typename)
  )
    return false;

  // prevent versioning with assets that are still being uploaded
  if (isSourceUploading || isTargetUploading) return false;

  if (source.parent?.__typename === 'VersionStackAsset') return false;

  return canVersionChildren;
};

/** returns true if a valid version stack op is being peformed */
export const isValidVersionStackOp = (op: DndOperation) => {
  const { activeInfo, overInfo, draggingIds, isCSCProject } = op;
  if (!activeInfo || !overInfo) return false;
  if (overInfo.id === activeInfo.id) return false;
  if (overInfo.zone !== 'project-panel') return false;
  if (draggingIds.size !== 1) return false;

  const { source, target } = getSourceAndTarget(op);
  if (!source || !target) return false;

  const assetParentInfo = getAssetParent(overInfo.id, op.cache);
  const parentFolderId =
    assetParentInfo?.parent?.__typename === 'VersionStackAsset'
      ? assetParentInfo.parent.parent?.id ?? ''
      : assetParentInfo?.parent?.id ?? '';

  return canStackAssetsAsVersions({
    source,
    target,
    isCSCProject,
    sourceFiletype: isCSCProject
      ? getAssetFiletype(source.id, op.cache)?.filetype
      : undefined,
    targetFiletype: isCSCProject
      ? getAssetFiletype(target.id, op.cache)?.filetype
      : undefined,
    isSourceUploading: isAssetUploading(source.id),
    isTargetUploading: isAssetUploading(target.id),
    canVersionChildren:
      getFolderPermissions(parentFolderId, op.cache)?.permissions
        ?.canVersionChildren ?? false,
  });
};

/** returns true if a valid move op is being peformed */
export const isValidMoveOp = (op: DndOperation) => {
  const { activeInfo, overInfo, draggingIds } = op;
  if (!activeInfo || !overInfo) return false;
  const { target } = getSourceAndTarget(op);

  const isShareZone =
    overInfo.zone === 'share-builder' ||
    activeInfo.zone === 'share-builder' ||
    overInfo.zone === 'share-review' ||
    activeInfo.zone === 'share-review';

  if (isShareZone) return false;

  /**
   * if we dont know anything about the target (eg it hasnt been hydrated)
   * and we're sure it's in the project nav
   * return true to allow the check to fall to the BE
   *
   * @todo (FIND-1239): @wcollado to fix this
   * */
  if (
    (!target || Object.values(target).length === 0) &&
    overInfo.zone === 'project-nav'
  )
    return true;

  /** prevent moving into a non-folder */
  if (target?.__typename !== 'FolderAsset') return false;

  const sources = Array.from(draggingIds).map((id) =>
    getAssetParent(id, op.cache)
  );

  /** prevent any folder moving into itself */
  if (sources.some((source) => source?.id === target.id)) return false;

  /** prevent any asset moving into its parent */
  if (sources.some((source) => source?.parent?.id === target.id)) return false;

  /** prevent any folder moving into a descendant of itself */
  const folderSourceIds = sources
    .filter((source) => source && source?.__typename === 'FolderAsset')
    .map((source) => source!.id);

  const traverseTargetParent = (id: string) => {
    if (!id) return false;
    const parentId = getAssetParent(id, op.cache)?.parent?.id;
    if (!parentId) return false;
    if (folderSourceIds.includes(parentId)) return true;

    return traverseTargetParent(parentId);
  };
  const targetAncestorInSources = traverseTargetParent(target.id);
  return !targetAncestorInSources;
};

export const getSourceAndTarget = (op: DndOperation) => {
  const source = op.activeInfo
    ? getAssetParent(op.activeInfo.id, op.cache)
    : null;
  const target = op.overInfo ? getAssetParent(op.overInfo.id, op.cache) : null;

  return { source, target };
};

const getAssetParent = (assetId: string, cache: ApolloCache<any>) =>
  cache.readFragment<AssetParentFragment>({
    fragment: AssetParent,
    id: `Asset:${assetId}`,
  });

const getFolderPermissions = (folderId: string, cache: ApolloCache<any>) =>
  cache.readFragment<FolderPermissionsFragment>({
    fragment: FolderPermissions,
    fragmentName: 'FolderPermissions',
    id: cache.identify({
      __typename: 'FolderAsset',
      id: folderId,
    }),
  });

const getAssetFiletype = (assetId: string, cache: ApolloCache<any>) =>
  cache.readFragment<AssetFiletypeFragment>({
    fragment: AssetFiletype,
    id: `Asset:${assetId}`,
  });

export const isReorderOp = (op: Omit<DndOperation, 'cache'>) =>
  getReorderId(op.overInfo?.id) !== undefined;

const REORDER_SENTINEL = 'REORDER-';
const REORDER_AFTER_SENTINEL = 'REORDER-AFTER-';
export const getReorderDroppableId = (
  beforeId: string | null | undefined,
  afterId: string | null | undefined
) => {
  if (afterId !== undefined) return `${REORDER_AFTER_SENTINEL}${afterId}`;
  if (beforeId !== undefined) return `${REORDER_SENTINEL}${beforeId}`;
  return ``;
};

export const getReorderId = (id?: string) => {
  if (!id) return undefined;
  let beforeId;
  let afterId;
  if (id.includes(REORDER_AFTER_SENTINEL))
    afterId = id.split(REORDER_AFTER_SENTINEL)[1];
  else if (id.includes(REORDER_SENTINEL)) {
    beforeId = id.split(REORDER_SENTINEL)[1];
  }

  if (beforeId === undefined && afterId === undefined) return undefined;
  return {
    afterId: afterId === 'null' ? null : afterId,
    beforeId: beforeId === 'null' ? null : beforeId,
  };
};

const isAssetUploading = (id: string) => {
  const transferState = state.transfers.transferJobTasks.get(id);

  if (!transferState) return false;

  return transferState.status <= TransferJobTaskStatus.UPLOADING;
};
